import {
  WSButton,
  WSCard,
  WSDivider,
  WSElement,
  WSFlexBox,
  WSIcon,
  WSInfoBox,
  WSList,
  WSPill,
  WSText
} from "@wingspanhq/fe-component-library";
import { IPayerTaxFormResponse } from "@wingspanhq/payments/dist/interfaces";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import React, { useMemo } from "react";
import {
  createTaxFormSubmissionListRequest,
  getTaxFormSubmissionViewDefaultFilters
} from "../../../../../query/search/taxForm/queries/filtering";
import { useQueryTaxFormSubmissionRows } from "../../../../../query/search/taxForm/queries/useQueryTaxFormSubmissionRows";
import { WSQueries } from "../../../../../query/WSQuery";
import { TaxFormSubmissionRequest } from "../../../../../services/search";
import { EmptyComponent } from "../../../../../shared/components/EmptyComponent";
import { useGoToTaxFormDetails } from "../../../paths";
import { selector1099Total } from "../../../selectors/selector1099Total";
import { selectorTaxFormRecipientName } from "../../../selectors/selectorTaxFormRecipientName";
import { useCurrentTaxFilingYear } from "../../../utils/useCurrentTaxFilingYear";

type Props = {
  taxForm: IPayerTaxFormResponse;
  submissionId: string;
};

export const GroupedForms: React.FC<Props> = ({ taxForm, submissionId }) => {
  const { groupSize, canViewAllFormsInGroup, isGrouped, groupIds } = taxForm;

  const currentYear = useCurrentTaxFilingYear();
  const goToTaxFormDetails = useGoToTaxFormDetails();

  const defaultFilters = getTaxFormSubmissionViewDefaultFilters();

  const currentRequest = useMemo((): TaxFormSubmissionRequest => {
    return createTaxFormSubmissionListRequest(currentYear, {
      ...defaultFilters,
      groupIds
    });
  }, [groupIds]);

  const queryTaxFormSubmissionRows = useQueryTaxFormSubmissionRows(
    currentRequest,
    {
      enabled: isGrouped && !!groupIds
    }
  );

  return (
    <WSQueries
      queries={{ queryTaxFormSubmissionRows }}
      renderLoader={EmptyComponent}
      renderErrors={EmptyComponent}
    >
      {({ queryTaxFormSubmissionRowsData }) => {
        const otherCount = groupSize ? groupSize - 1 : 0;
        const combinedTotal = canViewAllFormsInGroup
          ? queryTaxFormSubmissionRowsData.reduce((acc, taxFormSubmission) => {
              const total = selector1099Total(taxFormSubmission?.taxForm) || 0;

              return acc + total;
            }, 0)
          : 0;

        return (
          <WSCard
            divider
            header={{
              label: {
                text: "Grouped forms"
              }
            }}
          >
            <WSList gap="XL">
              <WSInfoBox>
                This form is grouped with {otherCount} other form
                {otherCount > 1 ? "s" : ""} with the same Taxpayer
                Identification Number (TIN).
                {canViewAllFormsInGroup ? null : (
                  <> Only forms you have permissions to are shown here.</>
                )}
              </WSInfoBox>

              {queryTaxFormSubmissionRowsData.map(taxFormSubmission => {
                const name = selectorTaxFormRecipientName(taxFormSubmission);
                const nec1099Total = selector1099Total(
                  taxFormSubmission.taxForm
                );
                const isCurrentSubmission =
                  taxFormSubmission.taxFormSubmissionId === submissionId;

                return (
                  <WSElement key={taxFormSubmission.taxFormId}>
                    <WSText.ParagraphSm color="gray500" mb="XS">
                      {name}
                    </WSText.ParagraphSm>
                    <WSFlexBox.CenterY>
                      <WSText.ParagraphSm>
                        {nec1099Total
                          ? formatMoney(nec1099Total, "USD")
                          : "$ -"}
                      </WSText.ParagraphSm>
                      {isCurrentSubmission ? (
                        <WSFlexBox.CenterY justify="flex-start">
                          <WSIcon name="check" mr="XS" color="gray500" />
                          <WSText.ParagraphXs weight="medium" color="gray500">
                            This form
                          </WSText.ParagraphXs>
                        </WSFlexBox.CenterY>
                      ) : (
                        <WSButton.Link
                          icon="view"
                          size="S"
                          onClick={() => {
                            goToTaxFormDetails(
                              taxFormSubmission.taxFormId,
                              taxFormSubmission.taxFormSubmissionId
                            );
                          }}
                        >
                          View
                        </WSButton.Link>
                      )}
                    </WSFlexBox.CenterY>
                  </WSElement>
                );
              })}

              {combinedTotal ? (
                <>
                  <WSDivider />
                  <WSElement>
                    <WSText.ParagraphSm color="gray500" mb="XS">
                      Combined 1099 total
                    </WSText.ParagraphSm>
                    <WSFlexBox.CenterY justify="space-between">
                      <WSText.ParagraphSm>
                        {formatMoney(combinedTotal, "USD")}
                      </WSText.ParagraphSm>
                      {combinedTotal > 600 ? (
                        <WSPill text="Over $600 threshold" theme="neutral" />
                      ) : null}
                    </WSFlexBox.CenterY>
                  </WSElement>
                </>
              ) : null}
            </WSList>
          </WSCard>
        );
      }}
    </WSQueries>
  );
};
